/**
 * Split a work-status snapshot into the sections the Thoughts panel renders.
 *
 * Tasks are no longer the parent of activity: the agent maintains its own list,
 * and the server records the steps that actually ran, and neither is derived
 * from the other. Attributing a step to a task was measured at 20% correct, so
 * these are presented as what they are — two separate views of one turn.
 *
 * The two task sections are split by *who may say a task is done*. A `server`
 * task is only ever completed by a provider confirmation bound to an approval;
 * everything else is the agent's own account. Rendering both with the same tick
 * would restate a model claim as a verified fact, which is the failure this
 * whole design exists to leave behind.
 */

import { rowInRun, taskInRun } from './taskRuns'

const byPosition = (a, b) => (a.position ?? 0) - (b.position ?? 0)

const bySeq = (a, b) => {
  if (a.startedSeq == null) return b.startedSeq == null ? 0 : 1
  if (b.startedSeq == null) return -1
  return a.startedSeq - b.startedSeq
}

/**
 * Whether this snapshot predates agent-owned tasks, and so keeps the older
 * grouped rendering.
 *
 * Keyed on the *presence* of a planner-created task rather than the absence of
 * an agent-created one. A turn where the agent never wrote a list has no tasks
 * at all, and treating "no tasks" as legacy sent it down the grouped path,
 * where a task-less snapshot renders as bare unlabelled rows — which is exactly
 * what a turn with an uncooperative model looks like, and the case the sections
 * view exists to handle well.
 */
export function isLegacyShape(snapshot) {
  return (snapshot?.tasks ?? []).some((task) => !task.client_key)
}

export function workSections(snapshot, rows = [], runId = null) {
  const tasks = (snapshot?.tasks ?? []).filter((task) => taskInRun(runId, task))
  return {
    plan: tasks.filter((task) => task.authority !== 'server').sort(byPosition),
    confirmed: tasks.filter((task) => task.authority === 'server').sort(byPosition),
    steps: rows
      .filter((row) => row.status !== 'superseded' && rowInRun(runId, row.originRunId))
      .sort(bySeq),
    trimmed: Boolean(snapshot?.activity_evicted_ids?.length),
  }
}
